import { useEffect, useState } from "react";
import MuiDialog from "@mui/material/Dialog";
import { IconInfoCircle, IconX } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { useProductWarrantyOptions } from "../../hooks/useProductWarrantyOptions";

export interface WarrantySelection {
  productId: number;
  name: string;
  price: number;
}

interface WarrantySelectModalProps {
  open: boolean;
  productName: string;
  /**
   * product.template id to fetch `compatible_warranty_ids` for. Pass null to
   * skip fetching (e.g. modal closed).
   */
  productTemplateId: number | null;
  /** Currently selected warranty product id, or null for "no warranty". */
  selectedWarrantyProductId: number | null;
  onClose: () => void;
  onConfirm: (selection: WarrantySelection | null) => void;
}

const NO_WARRANTY_ID = -1;

export function WarrantySelectModal({
  open,
  productName,
  productTemplateId,
  selectedWarrantyProductId,
  onClose,
  onConfirm,
}: WarrantySelectModalProps) {
  const { data: options, isLoading } = useProductWarrantyOptions(
    open ? productTemplateId : null,
  );

  const [selectedId, setSelectedId] = useState<number>(
    selectedWarrantyProductId ?? NO_WARRANTY_ID,
  );

  useEffect(() => {
    if (open) {
      setSelectedId(selectedWarrantyProductId ?? NO_WARRANTY_ID);
    }
  }, [open, selectedWarrantyProductId]);

  const selectedOption =
    selectedId === NO_WARRANTY_ID
      ? null
      : (options ?? []).find((o) => o.id === selectedId) ?? null;

  function handleConfirm() {
    if (selectedOption == null) {
      onConfirm(null);
    } else {
      onConfirm({
        productId: selectedOption.variantId ?? selectedOption.id,
        name: selectedOption.name,
        price: selectedOption.price ?? 0,
      });
    }
    onClose();
  }

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          className: "!rounded-2xl !max-w-2xl !shadow-xl",
          sx: { backgroundColor: "var(--color-bg-default)", backgroundImage: "none" },
        },
      }}
    >
      <div className="flex items-start justify-between border-b border-borderDefault px-6 py-5">
        <div className="flex flex-col gap-0.5">
          <Heading variant="h2" tone="dark">
            Select Extended Warranty
          </Heading>
          <Text variant="bodySm" tone="muted">
            {productName}
          </Text>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-textMuted hover:text-textDark transition-colors"
        >
          <IconX size={20} />
        </button>
      </div>

      <div className="flex flex-col gap-3 overflow-y-auto px-6 py-5">
        {isLoading && (
          <Text variant="bodySm" tone="muted">
            Loading warranty options…
          </Text>
        )}

        {!isLoading && (options ?? []).length === 0 && (
          <Text variant="bodySm" tone="muted">
            No extended warranties are offered for this product.
          </Text>
        )}

        {!isLoading && (options ?? []).length > 0 && (
          <>
            <WarrantyChoice
              id={NO_WARRANTY_ID}
              name="No extended warranty"
              priceLabel="Selected"
              isSelected={selectedId === NO_WARRANTY_ID}
              onSelect={() => setSelectedId(NO_WARRANTY_ID)}
            />
            {(options ?? []).map((option) => (
              <WarrantyChoice
                key={option.id}
                id={option.id}
                name={option.name}
                description={option.description}
                priceLabel={
                  option.price != null
                    ? `+QAR ${option.price.toLocaleString()}`
                    : "Included"
                }
                showPerUnit={option.price != null && option.price > 0}
                isSelected={selectedId === option.id}
                onSelect={() => setSelectedId(option.id)}
              />
            ))}
          </>
        )}

        <div className="flex items-center gap-4 rounded-2xl bg-accentBlueBanner p-6">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-accentBlueBanner">
            <IconInfoCircle size={20} className="text-accentBlue" />
          </div>
          <div className="flex flex-col">
            <Text variant="bodyBold" className="text-accentBlue">
              Why extend your warranty?
            </Text>
            <Text variant="bodySm" className="text-accentBlue">
              Extended warranties provide peace of mind and protect your investment
              with enhanced support coverage, faster response times, and additional
              services.
            </Text>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-borderDefault px-6 py-4">
        <div className="flex items-center gap-1">
          <Text variant="bodySm" tone="body" inline>
            Selected:
          </Text>
          <Text variant="bodySmBold" tone="dark" inline>
            {selectedOption?.name ?? "No extended warranty"}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={handleConfirm}>
            Confirm Selection
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </MuiDialog>
  );
}

function WarrantyChoice({
  name,
  description,
  priceLabel,
  showPerUnit,
  isSelected,
  onSelect,
}: {
  id: number;
  name: string;
  description?: string | null;
  priceLabel: string;
  showPerUnit?: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-2 rounded-2xl border p-4 text-left transition-colors ${
        isSelected ? "border-textDark" : "border-borderDefault"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <div
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
              isSelected ? "border-textDark bg-textDark" : "border-borderDefault bg-bgWhite"
            }`}
          >
            {isSelected && <div className="h-2 w-2 rounded-full bg-bgWhite" />}
          </div>
          <div className="flex flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark">
              {name}
            </Text>
            {description && (
              <Text variant="bodySm" tone="body">
                {description}
              </Text>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-0">
          <Text variant="bodyBold" tone="dark">
            {priceLabel}
          </Text>
          {showPerUnit && (
            <Text variant="bodyXs" tone="dark">
              per unit
            </Text>
          )}
        </div>
      </div>
    </button>
  );
}
